import React from 'react';
import PropTypes from 'prop-types';

import {
  Cushion,
  Typography,
  Rectangle,
  Space,
  Shadow,
  Grid,
  GridItem,
  Radio,
} from '@pitchero/react-ui';
import Icon from '../../icon';
import { Trans } from '../../../lib/i18n';

const TaggingDescription = ({ maxTaggedMembers, minTaggedMembers }) => {
  if (maxTaggedMembers < 1) {
    return null;
  }

  const values = {
    min: minTaggedMembers,
    max: maxTaggedMembers,
  };

  let key = 'shop:product_page_tagging_description_single';

  if (maxTaggedMembers > 1) {
    key =
      minTaggedMembers === maxTaggedMembers
        ? 'shop:product_page_tagging_description'
        : 'shop:product_page_tagging_description_range';
  }

  return (
    <Typography preset="description" color="dustygrey">
      <Trans i18nKey={key} values={values}>
        {`Buy for ${maxTaggedMembers} members`}
      </Trans>
      <Space left="xsmall">
        {maxTaggedMembers === 1 ? (
          <Icon name="user" color="dismissalsHw" size={12} topOffset={2} />
        ) : (
          <Icon name="users" color="dismissalsHw" size={18} topOffset={4} />
        )}
      </Space>
    </Typography>
  );
};

TaggingDescription.propTypes = {
  maxTaggedMembers: PropTypes.number.isRequired,
  minTaggedMembers: PropTypes.number.isRequired,
};

const Variant = ({
  onSelect,
  selected,
  setupFee,
  label,
  amountDescription,
  includeTaggingDescription,
  maxTaggedMembers,
  minTaggedMembers,
  inStock,
}) => {
  if (!inStock) {
    return (
      <Space bottom="small">
        <Rectangle style={{ opacity: '0.75' }} fill="mercury" radius="rounded">
          <Cushion vertical="18px" horizontal="medium">
            <Grid columns="100fr auto" columnGap="small">
              <GridItem>
                <Space bottom="xsmall">
                  <Typography color="dovegrey" preset="body--small" component="div">
                    {label}
                  </Typography>
                </Space>
                {includeTaggingDescription && (
                  <TaggingDescription
                    maxTaggedMembers={maxTaggedMembers}
                    minTaggedMembers={minTaggedMembers}
                  />
                )}
                {setupFee && (
                  <Typography preset="subtitle--small" component="div">
                    {setupFee}
                  </Typography>
                )}
                <Typography preset="subtitle--small" component="div">
                  {amountDescription}
                </Typography>
              </GridItem>
              <GridItem>
                <Typography preset="subtitle--small" component="div">
                  <Trans i18nKey="common:out_of_stock">Out of stock</Trans>
                </Typography>
              </GridItem>
            </Grid>
          </Cushion>
        </Rectangle>
      </Space>
    );
  }

  return (
    <Space bottom="small">
      <Shadow shadow={selected ? 'default' : null}>
        <Rectangle
          fill={selected ? 'white' : 'mercury'}
          onClick={onSelect}
          radius="rounded"
          onHover={{ fill: 'white' }}
        >
          <Cushion vertical="18px" horizontal="medium">
            <Grid columns="100fr auto" columnGap="small">
              <GridItem>
                <Space bottom="xsmall">
                  <Typography color="dovegrey" preset="body--small" component="div">
                    {label}
                  </Typography>
                </Space>
                {includeTaggingDescription && (
                  <TaggingDescription
                    maxTaggedMembers={maxTaggedMembers}
                    minTaggedMembers={minTaggedMembers}
                  />
                )}
                {setupFee && (
                  <Typography preset="subtitle--small" component="div">
                    {setupFee}
                  </Typography>
                )}
                <Typography preset="subtitle--small" component="div">
                  {amountDescription}
                </Typography>
              </GridItem>
              <GridItem alignSelf="center">
                <Radio checked={selected} checkedBgColor="primary" style={{ boxShadow: 'none' }} />
              </GridItem>
            </Grid>
          </Cushion>
        </Rectangle>
      </Shadow>
    </Space>
  );
};

Variant.defaultProps = {
  setupFee: null,
};

Variant.propTypes = {
  selected: PropTypes.bool.isRequired,
  inStock: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  setupFee: PropTypes.string,
  amountDescription: PropTypes.string.isRequired,
  includeTaggingDescription: PropTypes.bool.isRequired,
  maxTaggedMembers: PropTypes.number.isRequired,
  minTaggedMembers: PropTypes.number.isRequired,
};

export default Variant;
